import { HttpClient, HttpParams } from '@angular/common/http';
import { AuthService } from 'app/security/auth.service';
import { Subject } from 'rxjs';
import { PolicyParamDTO } from 'app/header-footer-template/model/PolicyParamDTO';
import { saveAs } from 'file-saver';
import { ToastrService } from 'ngx-toastr';
import { DomainService } from './domain-service';
import { ResetService } from './reset-service';
import { CommonsText } from 'app/commons/CommonsText';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common/http";
import * as i2 from "../security/auth.service";
import * as i3 from "ngx-toastr";
import * as i4 from "./domain-service";
import * as i5 from "./reset-service";
var PolicyService = /** @class */ (function () {
    function PolicyService(http, authService, toastr, domainService, resetService) {
        var _this = this;
        this.http = http;
        this.authService = authService;
        this.toastr = toastr;
        this.domainService = domainService;
        this.resetService = resetService;
        this.oldParams = new PolicyParamDTO();
        // Una única búsqueda cacheada (clave documento+entidad) de la que salen todos los
        // flags de visibilidad que dependen de ser tomador. La caché se limpia en el logout.
        this.takerSummaryPromise = null;
        this.takerSummaryKey = null;
        // Asistencia Jurídica va aparte porque además exige que la póliza esté en vigor, cosa
        // que takerSummary() no distingue. Se cachea igual, por documento + entidad.
        this.legalAssistancePromise = null;
        this.legalAssistanceKey = null;
        // Al hacer logout limpiamos la caché para no reutilizarla en una sesión posterior
        // (aunque vuelva a entrar el mismo usuario/entidad).
        this.resetService.observerLogout().subscribe(function () {
            _this.takerSummaryPromise = null;
            _this.takerSummaryKey = null;
            _this.legalAssistancePromise = null;
            _this.legalAssistanceKey = null;
        });
    }
    PolicyService.prototype.setPageSize = function (value) {
        this.pageSizeChange.next(value);
    };
    PolicyService.prototype.setPageOffset = function (value) {
        this.pageOffsetChange.next(value);
    };
    PolicyService.prototype.setPageFilter = function (value) {
        this.pageFilterChange.next(value);
    };
    PolicyService.prototype.observerPageSizeChange = function () {
        this.pageSizeChange = new Subject();
        return this.pageSizeChange.asObservable();
    };
    PolicyService.prototype.observerPageOffset = function () {
        this.pageOffsetChange = new Subject();
        return this.pageOffsetChange.asObservable();
    };
    PolicyService.prototype.observerPageFilter = function () {
        this.pageFilterChange = new Subject();
        return this.pageFilterChange.asObservable();
    };
    PolicyService.prototype.getCurrent = function () {
        var url = this.domainService.backendUrlWithContext + '/policies/current/' + this.authService.getSelectedEntity();
        return this.http.get(url).toPromise();
    };
    /**
     * Recupera las pólizas de la persona logada (/policies/me).
     */
    PolicyService.prototype.getMyPolicies = function () {
        var url = this.domainService.backendUrlWithContext + '/policies/me';
        return this.http.get(url).toPromise();
    };
    /**
     * Resumen de las pólizas del cliente en las que es TOMADOR, en la entidad activa.
     *
     * Es una única llamada a /policies/search de la que cuelgan todos los flags de
     * visibilidad, en lugar de una búsqueda por flag. Basta con pedir una página de 1
     * porque el backend calcula el facet 'product' sobre el resultado filtrado completo,
     * antes de paginar (ver SearchPageablePoliciesBySpringUserCoreCmd).
     *
     * La condición de tomador se resuelve en el backend (isTaker) y no con los productsIds
     * del token, que no distinguen entre tomador y asegurado. El resultado se cachea por
     * documento + entidad, así que basta una llamada por sesión y aseguradora.
     */
    PolicyService.prototype.takerSummary = function () {
        var _this = this;
        var user = this.authService.getUser();
        var documentNumber = user ? user.documentNumber : '';
        var key = documentNumber + '|' + this.authService.getSelectedEntity();
        if (this.takerSummaryPromise !== null && this.takerSummaryKey === key) {
            return this.takerSummaryPromise;
        }
        this.takerSummaryKey = key;
        var params = new PolicyParamDTO();
        params.pageOffset = 0;
        params.pageSize = 1;
        params.isTaker = true;
        this.takerSummaryPromise = this.search(params)
            .then(function (success) {
            var products = (success && success.product) ? success.product : [];
            var total = (success && success.totalElements) ? success.totalElements : 0;
            return {
                isTaker: total > 0,
                productIds: products.map(function (p) { return p.id; })
            };
        })
            .catch(function () {
            // No cachear el fallo: permite reintentar en la siguiente carga.
            _this.takerSummaryPromise = null;
            _this.takerSummaryKey = null;
            return { isTaker: false, productIds: [] };
        });
        return this.takerSummaryPromise;
    };
    /** Indica si el cliente tiene alguna póliza en calidad de tomador. */
    PolicyService.prototype.isTaker = function () {
        return this.takerSummary().then(function (summary) { return summary.isTaker; });
    };
    /** Indica si el cliente tiene acceso a "Mis beneficios". */
    PolicyService.prototype.hasBenefitProducts = function () {
        return this.hasAnyProduct(PolicyService.BENEFIT_PRODUCT_IDS);
    };
    /** Indica si el cliente tiene acceso a Legaliboo. */
    PolicyService.prototype.hasLegalibooProducts = function () {
        return this.hasAnyProduct(PolicyService.LEGALIBOO_PRODUCT_IDS);
    };
    /**
     * Indica si el cliente es tomador de alguna póliza de Asistencia Jurídica Familiar (DJ29)
     * **en vigor**. No sale del facet de takerSummary() porque ese no filtra por situación: una
     * DJ29 anulada mostraría "Mis asegurados" y "Mis consultas" con la pantalla vacía detrás.
     * Reutiliza la misma búsqueda que alimenta el combo de alta y se cachea por documento +
     * entidad, así que es una llamada por sesión y aseguradora.
     */
    PolicyService.prototype.hasLegalAssistanceProduct = function () {
        var _this = this;
        var user = this.authService.getUser();
        var documentNumber = user ? user.documentNumber : '';
        var key = documentNumber + '|' + this.authService.getSelectedEntity();
        if (this.legalAssistancePromise !== null && this.legalAssistanceKey === key) {
            return this.legalAssistancePromise;
        }
        this.legalAssistanceKey = key;
        this.legalAssistancePromise = this.getLegalAssistancePolicies()
            .then(function (list) { return list.length > 0; })
            .catch(function () {
            // No cachear el fallo: permite reintentar en la siguiente carga.
            _this.legalAssistancePromise = null;
            _this.legalAssistanceKey = null;
            return false;
        });
        return this.legalAssistancePromise;
    };
    PolicyService.prototype.hasAnyProduct = function (productIds) {
        return this.takerSummary()
            .then(function (summary) { return summary.productIds.some(function (id) { return productIds.indexOf(id) > -1; }); });
    };
    /**
     * Recupera las pólizas de Asistencia Jurídica Familiar (DJ29) **en vigor** de las que el
     * usuario es tomador, mediante /policies/search. Es la fuente del combo de alta de
     * asegurados: sobre una póliza anulada no se puede dar de alta a nadie.
     */
    PolicyService.prototype.getLegalAssistancePolicies = function () {
        var params = new PolicyParamDTO();
        params.pageOffset = 0;
        params.pageSize = 9999;
        params.product = [{ id: PolicyService.LEGAL_ASSISTANCE_PRODUCT_ID }];
        params.statusType = [{ id: PolicyService.POLICY_STATUS_ACTIVE }];
        params.isTaker = true;
        return this.search(params)
            .then(function (success) { return (success && success.content) ? success.content : []; });
    };
    PolicyService.prototype.getRelation = function () {
        var _this = this;
        this.getCurrent()
            .then(function (success) {
            var local = 'ASEG';
            for (var _i = 0, _a = success.response; _i < _a.length; _i++) {
                var policy = _a[_i];
                if (policy.isTaker) {
                    local = 'TOMA';
                    break;
                }
            }
            localStorage.setItem('relationType', local);
        }).catch(function (error) {
            _this.toastr.error(CommonsText.ERROR_SERVICIO_POLIZAS, CommonsText.ERROR_DE_OPERACION, {
                timeOut: 5000
            });
        });
    };
    PolicyService.prototype.getPolicyByNumber = function (policyNumber) {
        var url = this.domainService.backendUrlWithContext + '/policies/policy/' + policyNumber;
        return this.http.get(url).toPromise();
    };
    PolicyService.prototype.getLegal = function () {
        var url = this.domainService.backendUrlWithContext + '/policies/legal/' + this.authService.getSelectedEntity();
        return this.http.get(url).toPromise();
    };
    PolicyService.prototype.getMediators = function () {
        var url = this.domainService.backendUrlWithContext + '/policies/mediators/' + this.authService.getSelectedEntity();
        return this.http.get(url).toPromise();
    };
    PolicyService.prototype.getMediator = function (mediatorId) {
        var url = this.domainService.backendUrlWithContext + '/policies/mediator/' + mediatorId;
        return this.http.get(url).toPromise();
    };
    PolicyService.prototype.haveParameterChanges = function (params) {
        if ((undefined !== this.oldParams.statusType &&
            undefined !== params.statusType &&
            this.oldParams.statusType.length !== params.statusType.length) ||
            (this.oldParams.policyCollective !== params.policyCollective) ||
            (this.oldParams.policyNumber !== params.policyNumber) ||
            (undefined !== this.oldParams.product &&
                undefined !== params.product &&
                this.oldParams.product.length !== params.product.length)) {
            this.oldParams.statusType = Object.assign([], params.statusType);
            this.oldParams.policyItems = Object.assign([], params.policyItems);
            this.oldParams.policyCollective = params.policyCollective;
            this.oldParams.policyNumber = params.policyNumber;
            this.oldParams.product = Object.assign([], params.product);
            return true;
        }
        return false;
    };
    PolicyService.prototype.search = function (params) {
        var httpParams = new HttpParams();
        if (undefined !== params.pageOffset) {
            if (!this.haveParameterChanges(params)) {
                httpParams = httpParams.append('pageOffset', params.pageOffset.toString());
            }
            else {
                httpParams = httpParams.append('pageOffset', '0');
            }
        }
        if (undefined !== params.pageSize) {
            httpParams = httpParams.append('pageSize', params.pageSize.toString());
        }
        if ((undefined !== params.startDate) && (null !== params.startDate) && ('' !== params.startDate)) {
            httpParams = httpParams.append('startDate', params.startDate);
        }
        if ((undefined !== params.endDate) && (null !== params.endDate) && ('' !== params.endDate)) {
            httpParams = httpParams.append('endDate', params.endDate);
        }
        if ((undefined !== params.policyNumber) && (null !== params.policyNumber) && ('' !== params.policyNumber.trim())) {
            httpParams = httpParams.append('policyNumber', params.policyNumber);
        }
        if ((undefined !== params.policyCollective) && (null !== params.policyCollective) && ('' !== params.policyCollective.trim())) {
            httpParams = httpParams.append('policyCollective', params.policyCollective);
        }
        if (undefined !== params.statusType) {
            for (var i = 0; i < params.statusType.length; i++) {
                httpParams = httpParams.append('statusType', params.statusType[i].id);
            }
        }
        if (undefined !== params.product) {
            for (var i = 0; i < params.product.length; i++) {
                httpParams = httpParams.append('product', params.product[i].id);
            }
        }
        if (undefined !== params.policyItems) {
            for (var i = 0; i < params.policyItems.length; i++) {
                httpParams = httpParams.append('policyItems', params.policyItems[i].id);
            }
        }
        if ((undefined !== params.isTaker) && (null !== params.isTaker)) {
            httpParams = httpParams.append('isTaker', params.isTaker.toString());
        }
        var url = this.domainService.backendUrlWithContext + '/policies/search/' + this.authService.getSelectedEntity();
        return this.http.get(url, { params: httpParams }).toPromise();
    };
    PolicyService.prototype.getContractFile = function (policyNumber) {
        var _this = this;
        this.toastr.info(CommonsText.PREPARANDO_ARCHIVO_DESCARGA, CommonsText.SERVICIO_DE_DOCUMENTOS, {
            timeOut: 12000
        });
        var url = this.domainService.backendUrlWithContext + '/policies/document/' + policyNumber;
        this.http.get(url, { observe: 'response', responseType: 'blob' }).subscribe(function (resp) {
            _this.saveToFileSystem(resp);
        }, function (err) {
            _this.toastr.error(CommonsText.FALLO_AL_DESCARGAR, CommonsText.SERVICIO_DE_DOCUMENTOS, {
                timeOut: 5000
            });
        });
    };
    PolicyService.prototype.saveToFileSystem = function (response) {
        var contentType = response.headers.get('Content-Type');
        var contentDispositionHeader = response.headers.get('Content-Disposition');
        var parts = contentDispositionHeader.split(';');
        var filename = parts[1].split('=')[1].split('\"').join('');
        var blob = new Blob([response.body], { type: contentType });
        saveAs(blob, filename);
        this.toastr.success(CommonsText.ARCHIVO_DESCARGADO_CORRECTAMENTE, CommonsText.SERVICIO_DE_DOCUMENTOS, {
            timeOut: 5000
        });
    };
    PolicyService.prototype.getHirePolicyDataByNumber = function (policyNumber) {
        var url = this.domainService.backendUrlWithContext + '/policies/hireMoreInformation/' + policyNumber;
        return this.http.get(url).toPromise();
    };
    PolicyService.prototype.onHireMoreFormSubmit = function (params) {
        var form = new FormData();
        if ((undefined !== params.policyNumber) && (null !== params.policyNumber)) {
            form.append('policyNumber', params.policyNumber);
        }
        if ((undefined !== params.modificationDate) && (null !== params.modificationDate)) {
            form.append('modificationDate', params.modificationDate);
        }
        if ((undefined !== params.observation) && (null !== params.observation)) {
            form.append('observation', params.observation);
        }
        var url = this.domainService.backendUrlWithContext + '/policies/hireMore';
        return this.http.post(url, form).toPromise();
    };
    PolicyService.prototype.changeIdentifier = function (params) {
        var form = new FormData();
        form.append('polizsec', params.policyNumber);
        form.append('oldIdentifiyer', params.identifier);
        form.append('newIdentifiyer', params.newIdentifier);
        form.append('modificationDate', params.modificationDate);
        var url = this.domainService.backendUrlWithContext + '/policies/changeIdentifier';
        return this.http.post(url, form).toPromise();
    };
    // Producto "Asistencia Jurídica Familiar".
    PolicyService.LEGAL_ASSISTANCE_PRODUCT_ID = 'DJ29';
    // Productos que dan acceso a "Mis beneficios".
    PolicyService.BENEFIT_PRODUCT_IDS = ['PP09', 'PP11', 'DJ13', 'DJ29'];
    // Productos que dan acceso a Legaliboo.
    PolicyService.LEGALIBOO_PRODUCT_IDS = ['PP09', 'PP11'];
    // Situación "En vigor" de Innova, tal como la espera el filtro statusType de la búsqueda.
    PolicyService.POLICY_STATUS_ACTIVE = 'V';
    PolicyService.ngInjectableDef = i0.defineInjectable({ factory: function PolicyService_Factory() { return new PolicyService(i0.inject(i1.HttpClient), i0.inject(i2.AuthService), i0.inject(i3.ToastrService), i0.inject(i4.DomainService), i0.inject(i5.ResetService)); }, token: PolicyService, providedIn: "root" });
    return PolicyService;
}());
export { PolicyService };
